import { SandpackPreview, SandpackProvider } from "@codesandbox/sandpack-react";
import { AlertTriangle } from "lucide-react";
import PropTypes from "prop-types";
import { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { essentialShadcnComponents } from "./shadcn";

// Helper function to clean code content by removing markdown artifacts
function cleanCodeContent(content) {
  if (!content || typeof content !== "string") return "";

  let cleaned = content;

  // Remove code fence tags (```python, ```javascript, etc.)
  cleaned = cleaned.replace(/^```[\w]*\s*\n?/gm, "");
  cleaned = cleaned.replace(/\n?```\s*$/gm, "");

  // Remove leading/trailing whitespace
  cleaned = cleaned.trim();

  return cleaned;
}

// Helper function to extract actual code content from BlockNote JSON or other formats
function extractCodeContent(content) {
  if (!content) return "";

  // If content is a string, try to parse it
  if (typeof content === "string") {
    // Check if it's BlockNote JSON
    if (content.startsWith("[") || content.startsWith("{")) {
      try {
        const parsed = JSON.parse(content);

        // Handle array of blocks (BlockNote format)
        if (Array.isArray(parsed)) {
          // Look for code blocks first
          const codeBlocks = parsed.filter(
            (block) => block.type === "codeBlock"
          );
          if (codeBlocks.length > 0) {
            // Return the content of the first code block
            const codeBlock = codeBlocks[0];
            if (
              codeBlock.content &&
              codeBlock.content[0] &&
              codeBlock.content[0].text
            ) {
              return cleanCodeContent(codeBlock.content[0].text);
            }
          }

          // If no code blocks, concatenate all text content
          let extractedText = "";
          parsed.forEach((block) => {
            if (block.content && Array.isArray(block.content)) {
              block.content.forEach((item) => {
                if (item.type === "text" && item.text) {
                  extractedText += item.text + "\n";
                }
              });
            }
          });

          return cleanCodeContent(extractedText.trim());
        }

        // Handle single block object
        if (
          parsed.type === "codeBlock" &&
          parsed.content &&
          parsed.content[0]
        ) {
          return cleanCodeContent(parsed.content[0].text || "");
        }
      } catch {
        // Not JSON, return as-is but clean it
        return cleanCodeContent(content);
      }
    }

    // Return string content as-is but clean it
    return cleanCodeContent(content);
  }

  // If content is already an object/array, try to extract from it
  if (Array.isArray(content)) {
    const codeBlocks = content.filter((block) => block.type === "codeBlock");
    if (
      codeBlocks.length > 0 &&
      codeBlocks[0].content &&
      codeBlocks[0].content[0]
    ) {
      return cleanCodeContent(codeBlocks[0].content[0].text || "");
    }

    // Extract all text content
    let extractedText = "";
    content.forEach((block) => {
      if (block.content && Array.isArray(block.content)) {
        block.content.forEach((item) => {
          if (item.type === "text" && item.text) {
            extractedText += item.text + "\n";
          }
        });
      }
    });

    return cleanCodeContent(extractedText.trim());
  }

  return cleanCodeContent(String(content));
}

// Add custom styles for Sandpack
const sandpackStyles = `
.sp-wrapper {
  height: 100% !important;
  min-height: calc(100vh - 64px) !important;
}

.sp-layout {
  height: 100% !important;
  min-height: calc(100vh - 64px) !important;
  flex-grow: 1 !important;
}

.sp-preview-container {
  height: 100% !important;
  min-height: calc(100vh - 64px) !important;
}

.sp-stack {
  height: 100% !important;
}

.fullscreen .sp-wrapper,
.fullscreen .sp-layout,
.fullscreen .sp-preview-container,
.fullscreen .sp-stack {
  min-height: calc(100vh - 40px) !important;
}
`;

const coldarkDarkSandpackTheme = {
  colors: {
    surface1: "#111b27", // Background
    surface2: "#1a2736", // Editor background
    surface3: "#213346", // Button background
    clickable: "#8da1b9", // Text on buttons
    base: "#e3eaf2", // Text and foreground
    disabled: "#3b4d61", // Disabled text
    hover: "#213346", // Hover state
    accent: "#47a3f3", // Accent color (links, etc)
    error: "#f0a5a5", // Error text
    errorSurface: "#351414", // Error background
  },
  syntax: {
    plain: "#e3eaf2", // Default text
    comment: { color: "#8da1b9", fontStyle: "italic" }, // Comments
    keyword: "#e39972", // Keywords
    tag: "#e39972", // Tags
    punctuation: "#8da1b9", // Punctuation
    definition: "#41a1c0", // Function/method names
    property: "#5db0d7", // Properties
    static: "#47a3f3", // Static values
    string: "#7fc06e", // Strings
  },
  font: {
    body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    mono: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
    size: "14px",
    lineHeight: "1.5",
  },
};

const CUSTOM_DEPENDENCIES = {
  "@radix-ui/react-accordion": "^1.2.0",
  "@radix-ui/react-alert-dialog": "^1.1.1",
  "@radix-ui/react-aspect-ratio": "^1.1.0",
  "@radix-ui/react-avatar": "^1.1.0",
  "@radix-ui/react-checkbox": "^1.1.1",
  "@radix-ui/react-collapsible": "^1.1.0",
  "@radix-ui/react-dialog": "^1.1.1",
  "@radix-ui/react-dropdown-menu": "^2.1.1",
  "@radix-ui/react-hover-card": "^1.1.1",
  "@radix-ui/react-label": "^2.1.0",
  "@radix-ui/react-menubar": "^1.1.1",
  "@radix-ui/react-navigation-menu": "^1.2.0",
  "@radix-ui/react-popover": "^1.1.1",
  "@radix-ui/react-progress": "^1.1.0",
  "@radix-ui/react-radio-group": "^1.2.0",
  "@radix-ui/react-select": "^2.1.1",
  "@radix-ui/react-separator": "^1.1.0",
  "@radix-ui/react-slider": "^1.2.0",
  "@radix-ui/react-slot": "^1.1.0",
  "@radix-ui/react-switch": "^1.1.0",
  "@radix-ui/react-tabs": "^1.1.0",
  "@radix-ui/react-toast": "^1.2.1",
  "@radix-ui/react-toggle": "^1.1.0",
  "@radix-ui/react-toggle-group": "^1.1.0",
  "@radix-ui/react-tooltip": "^1.1.2",
  // "@radix-ui/react-button": "^1.1.0",
  "@stripe/react-stripe-js": "^2.5.0",
  "@stripe/stripe-js": "^3.0.0",
  "class-variance-authority": "^0.7.0",
  "tailwind-merge": "^2.0.0",
  clsx: "^2.0.0",
  "lucide-react": "^0.292.0",
  "framer-motion": "^11.0.5",
  tailwindcss: "^3.3.0",
  postcss: "^8.4.31",
  autoprefixer: "^10.4.16",
  "tailwindcss-animate": "^1.0.7",
  lodash: "latest",
  mathjs: "latest",
  papaparse: "latest",
  "date-fns": "latest",
  axios: "latest",
};

// Helper function to safely replace imports in component code
const safeReplaceImports = (componentCode, componentName) => {
  if (!componentCode || typeof componentCode !== "string") {
    console.warn(
      `Component ${componentName} is undefined or not a string:`,
      componentCode
    );
    return `// Component ${componentName} not available
export default function ${componentName}() {
  console.warn('${componentName} component is not available');
  return null;
}`;
  }

  try {
    return componentCode
      .replace(/from "\.\.\/\.\.\/lib\/utils"/g, 'from "../../lib/utils"')
      .replace(/from "\.\/button"/g, 'from "./button"')
      .replace(/from "\.\/toggle"/g, 'from "./toggle"');
  } catch (error) {
    console.error(`Error processing component ${componentName}:`, error);
    return `// Error processing component ${componentName}
export default function ${componentName}() {
  console.error('Error processing ${componentName}:', ${JSON.stringify(error.message)});
  return null;
}`;
  }
};

export const CUSTOM_FILES = {
  "/tailwind.config.js": {
    code: `/** @type {import('tailwindcss').Config} */
module.exports = {
  content: [
    "./src/**/*.{js,jsx}",
    "./**/*.{js,jsx}",
  ],
  darkMode: 'class',
  theme: {
    container: {
      center: true,
      padding: "2rem",
      screens: {
        "2xl": "1400px",
      },
    },
    extend: {
      colors: {
        border: "hsl(var(--border))",
        input: "hsl(var(--input))",
        ring: "hsl(var(--ring))",
        background: "hsl(var(--background))",
        foreground: "hsl(var(--foreground))",
        primary: {
          DEFAULT: "hsl(var(--primary))",
          foreground: "hsl(var(--primary-foreground))",
        },
        secondary: {
          DEFAULT: "hsl(var(--secondary))",
          foreground: "hsl(var(--secondary-foreground))",
        },
        destructive: {
          DEFAULT: "hsl(var(--destructive))",
          foreground: "hsl(var(--destructive-foreground))",
        },
        muted: {
          DEFAULT: "hsl(var(--muted))",
          foreground: "hsl(var(--muted-foreground))",
        },
        accent: {
          DEFAULT: "hsl(var(--accent))",
          foreground: "hsl(var(--accent-foreground))",
        },
        popover: {
          DEFAULT: "hsl(var(--popover))",
          foreground: "hsl(var(--popover-foreground))",
        },
        card: {
          DEFAULT: "hsl(var(--card))",
          foreground: "hsl(var(--card-foreground))",
        },
      },
      borderRadius: {
        lg: "var(--radius)",
        md: "calc(var(--radius) - 2px)",
        sm: "calc(var(--radius) - 4px)",
      },
      keyframes: {
        "accordion-down": {
          from: { height: "0" },
          to: { height: "var(--radix-accordion-content-height)" },
        },
        "accordion-up": {
          from: { height: "var(--radix-accordion-content-height)" },
          to: { height: "0" },
        },
      },
      animation: {
        "accordion-down": "accordion-down 0.2s ease-out",
        "accordion-up": "accordion-up 0.2s ease-out",
      },
    },
  },
  plugins: [require("tailwindcss-animate")],
}`,
  },
  "/postcss.config.js": {
    code: `module.exports = {
  plugins: {
    tailwindcss: {},
    autoprefixer: {},
  },
}`,
  },
  "/src/index.css": {
    code: `@tailwind base;
@tailwind components;
@tailwind utilities;

@layer base {
  :root {
    --background: 0 0% 100%;
    --foreground: 222.2 84% 4.9%;
    --card: 0 0% 100%;
    --card-foreground: 222.2 84% 4.9%;
    --popover: 0 0% 100%;
    --popover-foreground: 222.2 84% 4.9%;
    --primary: 222.2 47.4% 11.2%;
    --primary-foreground: 210 40% 98%;
    --secondary: 210 40% 96.1%;
    --secondary-foreground: 222.2 47.4% 11.2%;
    --muted: 210 40% 96.1%;
    --muted-foreground: 215.4 16.3% 46.9%;
    --accent: 210 40% 96.1%;
    --accent-foreground: 222.2 47.4% 11.2%;
    --destructive: 0 84.2% 60.2%;
    --destructive-foreground: 210 40% 98%;
    --border: 214.3 31.8% 91.4%;
    --input: 214.3 31.8% 91.4%;
    --ring: 222.2 84% 4.9%;
    --radius: 0.5rem;
  }

  .dark {
    --background: 222.2 84% 4.9%;
    --foreground: 210 40% 98%;
    --card: 222.2 84% 4.9%;
    --card-foreground: 210 40% 98%;
    --popover: 222.2 84% 4.9%;
    --popover-foreground: 210 40% 98%;
    --primary: 210 40% 98%;
    --primary-foreground: 222.2 47.4% 11.2%;
    --secondary: 217.2 32.6% 17.5%;
    --secondary-foreground: 210 40% 98%;
    --muted: 217.2 32.6% 17.5%;
    --muted-foreground: 215 20.2% 65.1%;
    --accent: 217.2 32.6% 17.5%;
    --accent-foreground: 210 40% 98%;
    --destructive: 0 62.8% 30.6%;
    --destructive-foreground: 210 40% 98%;
    --border: 217.2 32.6% 17.5%;
    --input: 217.2 32.6% 17.5%;
    --ring: 212.7 26.8% 83.9%;
  }
}

@layer base {
  * {
    @apply border-border;
  }
  body {
    @apply bg-background text-foreground;
  }
}`,
  },
  "/src/index.js": {
    code: `import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from '../App.js';

// Add dark class to html element
document.documentElement.classList.add('dark');

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);`,
  },
  "/lib/utils.js": {
    code:
      essentialShadcnComponents?.utils ||
      `import { clsx } from 'clsx'; import { twMerge } from 'tailwind-merge'; export function cn(...inputs) { return twMerge(clsx(inputs)); }`,
  },
  // Essential Shadcn UI Components Only
  "/components/ui/button.jsx": {
    code: safeReplaceImports(essentialShadcnComponents?.button, "Button"),
  },
  "/components/ui/card.jsx": {
    code: safeReplaceImports(essentialShadcnComponents?.card, "Card"),
  },
  "/components/ui/input.jsx": {
    code: safeReplaceImports(essentialShadcnComponents?.input, "Input"),
  },
  "/components/ui/label.jsx": {
    code: safeReplaceImports(essentialShadcnComponents?.label, "Label"),
  },
  "/components/ui/textarea.jsx": {
    code: safeReplaceImports(essentialShadcnComponents?.textarea, "Textarea"),
  },
  "/components/ui/avatar.jsx": {
    code: safeReplaceImports(essentialShadcnComponents?.avatar, "Avatar"),
  },
  "/components/ui/checkbox.jsx": {
    code: safeReplaceImports(essentialShadcnComponents?.checkbox, "Checkbox"),
  },
  "/components/ui/select.jsx": {
    code: safeReplaceImports(essentialShadcnComponents?.select, "Select"),
  },
  "/components/ui/radio-group.jsx": {
    code: safeReplaceImports(
      essentialShadcnComponents?.radioGroup,
      "RadioGroup"
    ),
  },
};

const SandpackErrorFallback = ({ error }) => {
  // Check if error is related to module imports
  const isImportError = error?.message?.includes('Could not find module') ||
                        error?.message?.includes('Module not found') ||
                        error?.message?.includes('Cannot resolve module');

  const hasLocalImport = error?.message?.includes('./components/') ||
                          error?.message?.includes('./lib/') ||
                          error?.message?.includes('../');

  return (
    <div className="p-4 flex flex-col items-center justify-center min-h-[300px] bg-red-950/20 border border-red-800/30 rounded-md">
      <AlertTriangle className="w-8 h-8 text-red-500 mb-2" />
      <h3 className="text-red-400 font-medium mb-1">
        {isImportError ? 'Import Error' : 'Preview Rendering Error'}
      </h3>

      {isImportError && hasLocalImport ? (
        <div className="text-sm text-gray-300 text-center max-w-md space-y-2">
          <p className="font-medium text-red-400">
            This code contains local component imports that aren&apos;t available in the sandbox.
          </p>
          <p>
            The preview runs in an isolated environment and can only access NPM packages.
          </p>
          <div className="mt-3 p-3 bg-zinc-900/50 rounded text-left text-xs">
            <p className="text-yellow-400 mb-2">❌ Don&apos;t use:</p>
            <code className="text-red-300">import &#123; Button &#125; from &quot;./components/ui/button&quot;</code>
            <p className="text-green-400 mt-3 mb-2">✅ Instead use:</p>
            <code className="text-green-300">import React from &quot;react&quot;</code>
            <p className="text-gray-400 mt-2">Create inline components or use NPM packages only.</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-300 text-center max-w-md">
          {isImportError
            ? 'Could not resolve one or more module imports. Make sure you&apos;re only importing from NPM packages.'
            : 'There was an issue loading the code preview. This could be due to syntax errors or browser compatibility issues.'
          }
        </p>
      )}

      {error?.message && (
        <details className="mt-3 w-full max-w-md">
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
            Show error details
          </summary>
          <pre className="mt-2 p-2 bg-zinc-900/50 rounded text-xs text-red-300 overflow-auto max-h-32">
            {error.message}
          </pre>
        </details>
      )}
    </div>
  );
};

SandpackErrorFallback.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
};

const CodePreview = ({ content = "", language: _language = "javascript" }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Extract actual code content from BlockNote JSON or other formats
  const actualContent = extractCodeContent(content);

  // Check if content contains React code
  const canShowPreview =
    actualContent.includes("import React") ||
    actualContent.includes("export default") ||
    actualContent.includes("from 'react'") ||
    actualContent.includes('from "react"');

  const _toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  if (!canShowPreview) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-zinc-950/50 border border-border/40 rounded-lg">
        <div className="text-center text-muted-foreground p-8">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
          <h3 className="font-medium mb-1">Preview not available</h3>
          <p className="text-sm">
            This code doesn&apos;t appear to be a React component. Preview is
            only available for React code.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative group rounded-lg border border-border/40 bg-zinc-950/50 backdrop-blur-sm shadow-md flex flex-col ${
        isFullScreen ? "fixed inset-0 z-50 rounded-none border-0" : ""
      }`}
      style={{
        height: isFullScreen ? "100vh" : "calc(100vh - 64px)",
      }}
    >
      {/* Header */}
      {/* <div className="flex items-center justify-between px-4 py-2 border-b border-border/40 bg-zinc-900/50">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground font-medium">
            Live Preview
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={toggleFullScreen}
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground border border-zinc-800 h-8 w-8 bg-transparent text-zinc-400 hover:text-zinc-100"
                >
                  {isFullScreen ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isFullScreen ? "Exit fullscreen" : "Fullscreen"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div> */}

      {/* Preview Content */}
      <div className={`flex-grow h-full ${isFullScreen ? "fullscreen" : ""}`}>
        <ErrorBoundary FallbackComponent={SandpackErrorFallback}>
          <style>{sandpackStyles}</style>
          <SandpackProvider
            theme={coldarkDarkSandpackTheme}
            template="react"
            files={{
              ...CUSTOM_FILES,
              "/App.js": actualContent
                .replace(
                  `import './styles/globals.css'`,
                  `import './src/index.css'`
                )
                .replace(
                  /import { cn } from '@\/lib\/utils'/g,
                  `import { cn } from './lib/utils'`
                )
                .replace(
                  /import { cn } from "..\/..\/lib\/utils"/g,
                  `import { cn } from './lib/utils'`
                )
                .replace(/from '@\/components\/ui\//g, 'from "./components/ui/')
                .replace(
                  /from "..\/..\/components\/ui\//g,
                  'from "./components/ui/'
                ),
            }}
            customSetup={{
              dependencies: {
                ...CUSTOM_DEPENDENCIES,
                tailwindcss: "^3.3.0",
                postcss: "^8.4.31",
                autoprefixer: "^10.4.16",
              },
            }}
            options={{
              showNavigator: false,
              showLineNumbers: false,
              showInlineErrors: true,
              wrapContent: true,
              editorHeight: isFullScreen ? "calc(100vh - 40px)" : "100%",
              showTabs: false,
              closableTabs: false,
              externalResources: [
                "https://unpkg.com/@tailwindcss/ui/dist/tailwind-ui.min.css",
              ],
            }}
            className="h-full flex-grow"
          >
            <SandpackPreview
              className="h-full"
              showOpenInCodeSandbox={false}
              showRefreshButton={true}
            />
          </SandpackProvider>
        </ErrorBoundary>
      </div>
    </div>
  );
};

CodePreview.propTypes = {
  content: PropTypes.string,
  language: PropTypes.string,
};

export default CodePreview;
